import React, { useState, useRef, useMemo, useEffect } from 'react';
import './companyList.css';
import useFetchAllCompanies from '../../../hooks/company/useFetchAllCompanies';
import CompanyCard from './CompanyCard/CompanyCard';
import FilterOrderPagination from '../../Shared/FilterOrderPagination/FilterOrderPagination';
import CompanySideFilter from './CompanySideFilter/CompanySideFilter';
import CompanySearchBar from './CompanySearchBar/CompanySearchBar';
import { filterIcon, trashIcon, refreshIcon } from '../../../utils/icons';
import {filterOptionsCompany} from '../../../helpers/filterOptions';
import Loading from '../../Shared/Loading/Loading';

const CompanyListView = () => {
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isShowingLoader, setIsShowingLoader] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isSidebarFilterOpen, setIsSidebarFilterOpen] = useState(false);
  const [clearSearchTrigger, setClearSearchTrigger] = useState(false);
  const [orderBy, setOrderBy] = useState('');
  const filterRef = useRef(null);
  const memoizedFilters = useMemo(() => filters, [filters]);
  const searchTimeoutRef = useRef(null);

  const { companies, isLoading, error, totalPages, totalCompanies } = useFetchAllCompanies({
    filters: memoizedFilters,
    searchTerm,
    page: currentPage,
    limit: 10,
    type: "company-search",
    orderBy
  });

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 500); // Wait 500ms after user stops typing

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [inputValue]);

  useEffect(() => {
    if (isLoading) {
      setIsShowingLoader(true);
    } else {
      const timer = setTimeout(() => {
        setIsShowingLoader(false);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsSidebarFilterOpen(false);
      }
    };

    if (isSidebarFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSidebarFilterOpen]);
  
  const handleFilterChange = (newFilters) => {
    
    const cleanedFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([key, value]) => value !== null && value !== '')
    );
     
    setSelectedFilters(newFilters); 
    setFilters(cleanedFilters);
    setCurrentPage(1);
    
  };

  const handleSearchInputChange = (term) => {
    setInputValue(term);
  };

  const handleSearchSubmit = () => {
    if (inputValue.trim()) {
      setSearchTerm(inputValue.trim());
      setCurrentPage(1);
      setHasSearched(true);
      setIsShowingLoader(true);
    } else {
      // If search is empty, clear the search and show all companies
      setSearchTerm('');
      setCurrentPage(1);
      setHasSearched(false);
    }
  };

  const handleSearchResultSelect = (company) => {
    setSearchTerm(company.name); 
    setCurrentPage(1); 
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const toggleSidebar = () => {
    setIsSidebarFilterOpen(!isSidebarFilterOpen);
  };

  const handleOrderChange = (newOrder) => {
    setOrderBy(newOrder); 
    setCurrentPage(1); 
  };

  const clearFilters = () => {
    setFilters({});
    setSelectedFilters({});
    setCurrentPage(1); 
    setSearchTerm('');
    setClearSearchTrigger((prev) => !prev); 
  } 

  if (isLoading || isShowingLoader) {
    return (
      <>
       <Loading text={'empresas'}/>
      </>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading companies: {error}</p>
      </div>
    );
  }

  return (
    <>
      <CompanySearchBar
        onSearch={handleSearchInputChange}
        onSubmit={handleSearchSubmit}
        searchTerm={inputValue}
        searchResults={companies || []} 
        loading={isLoading}
        error={error}
        onSelectResult={handleSearchResultSelect} 
      />
      <div className='company-list-view__list-filter'>
        <div ref={filterRef} className={`side-filter-container ${isSidebarFilterOpen ? 'open' : ''}`}>  
          <button onClick={clearFilters} className="company-list-view__clear-filters-button">
            {refreshIcon}
          </button>
         
          <CompanySideFilter 
            filters={filterOptionsCompany} 
            onFilterChange={handleFilterChange}  
            selectedFilters={selectedFilters}
          />
        </div>
        <div className="pagination-companies__company-list">
          <div className="company-list-view__icons-container">
            <button className="company-list-view__filter-icon" onClick={toggleSidebar}>
              {filterIcon}
            </button>
            <button onClick={clearFilters} className="job-posts__clear-filters-button-mobile">
              {refreshIcon}
            </button>
          </div>
          <FilterOrderPagination
            currentPage={currentPage}
            totalPages={totalPages} 
            onPageChange={handlePageChange} 
            data={companies || []}  
            itemsPerPage={10}  
            totalItems={totalCompanies}
            onOrderChange={handleOrderChange}
            type={'companies'}
            orderBy={orderBy}
          />
          <div className="company-list-view__company-list">
            {Array.isArray(companies) && companies.length > 0 ? (
              companies.map((company) => (
                <CompanyCard key={company.id} company={company} />
              ))
            ) : (
              <p>No companies available</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyListView;
